import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { z } from 'zod';
import { supabase, isConnectionError, getConnectionErrorMessage, debugUtils } from '../lib/supabase';
import { AuthError } from '@supabase/supabase-js';

const UserSchema = z.object({
  id: z.string().uuid(),
  username: z.string().min(1),
  photoURL: z.string().url(),
  bio: z.string(),
  email: z.string().email().nullable(),
  location: z.string(),
  followerCount: z.number().int().nonnegative(),
  followingCount: z.number().int().nonnegative(),
  isFollowedByUser: z.boolean(),
  is_verified: z.boolean().optional()
});

type User = z.infer<typeof UserSchema>;

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<void>;
  followUser: (userId: string) => Promise<void>;
  unfollowUser: (userId: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((error: any, context: string) => {
    debugUtils.errorLog(`${context} error:`, error);
    
    let errorMessage = 'An unexpected error occurred';
    
    if (isConnectionError(error)) {
      errorMessage = getConnectionErrorMessage(error);
    } else if (error instanceof AuthError) {
      if (error.message.includes('Invalid login credentials') || 
          error.message.includes('Email not confirmed') ||
          error.message.includes('Invalid email or password')) {
        errorMessage = 'Invalid email or password';
      } else {
        errorMessage = error.message || 'Authentication failed';
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    return errorMessage;
  }, []);

  const fetchUserProfile = useCallback(async (userId: string): Promise<User> => {
    try {
      debugUtils.debugLog('Fetching user profile for ID:', userId);
      
      // Validate userId parameter first
      if (!userId || typeof userId !== 'string') {
        throw new Error(`Invalid user ID provided: ${userId}`);
      }
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        debugUtils.errorLog('Profile fetch error:', profileError);
        throw profileError;
      }
      
      if (!profile) {
        throw new Error('Profile not found');
      }
      
      // Handle case where .single() returns an array instead of a single object
      const profileData = Array.isArray(profile) ? profile[0] : profile;
      
      if (!profileData) {
        throw new Error('Profile not found');
      }
      
      // Explicit validation of critical profile fields
      if (!profileData.id || typeof profileData.id !== 'string') {
        debugUtils.errorLog('Profile missing or invalid ID:', { profile: profileData, expectedId: userId });
        throw new Error(`Profile data integrity error: missing or invalid ID field. Expected: ${userId}, Got: ${profileData.id}`);
      }
      
      // Ensure the fetched profile ID matches the requested userId
      if (profileData.id !== userId) {
        debugUtils.errorLog('Profile ID mismatch:', { expected: userId, actual: profileData.id });
        throw new Error(`Profile ID mismatch: expected ${userId}, got ${profileData.id}`);
      }

      debugUtils.debugLog('Profile fetched successfully:', profileData);

      // Fetch follower and following counts in parallel
      const [
        { count: followerCount },
        { count: followingCount },
        { data: isFollowing }
      ] = await Promise.all([
        supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('following_id', userId),
        supabase
          .from('follows')
          .select('*', { count: 'exact', head: true })
          .eq('follower_id', userId),
        supabase
          .from('follows')
          .select('follower_id')
          .eq('follower_id', userId)
          .eq('following_id', userId)
          .maybeSingle()
      ]);

      // Ensure we have valid data before creating user object
      const userData = {
        id: profileData.id,
        username: profileData.username || `User_${profileData.id.slice(0, 8)}`,
        photoURL: profileData.photo_url && typeof profileData.photo_url === 'string' && profileData.photo_url.trim() 
          ? profileData.photo_url 
          : 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
        bio: profileData.bio || '',
        email: profileData.email && typeof profileData.email === 'string' ? profileData.email : null,
        location: profileData.location || '',
        followerCount: followerCount || 0,
        followingCount: followingCount || 0,
        isFollowedByUser: !!isFollowing,
        is_verified: profileData.is_verified || false
      };

      debugUtils.debugLog('User data prepared:', userData);
      
      // Validate the user data before returning
      const validatedUser = UserSchema.parse(userData);
      debugUtils.debugLog('User data validated successfully');
      
      return validatedUser;
    } catch (error) {
      debugUtils.errorLog('Error in fetchUserProfile:', error);
      
      // If it's a Zod validation error, provide more context
      if (error && typeof error === 'object' && 'issues' in error) {
        debugUtils.errorLog('Zod validation failed with issues:', error.issues);
        throw new Error(`Profile validation failed: ${JSON.stringify(error.issues)}`);
      }
      
      throw error;
    }
  }, []);

  const setUserAndClearError = useCallback((user: User | null) => {
    setCurrentUser(user);
    setError(null);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let initializationComplete = false;

    // Timeout to prevent infinite loading (30 seconds)
    initTimeoutRef.current = setTimeout(() => {
      if (!initializationComplete && isMounted) {
        debugUtils.errorLog('Auth initialization timeout - forcing completion');
        setIsLoading(false);
        setIsInitialized(true);
        setError('Authentication took too long. Please refresh the page.');
      }
    }, 30000);

    const initAuth = async () => {
      if (initializationComplete) return;

      try {
        debugUtils.debugLog('Initializing authentication...');

        // Add timeout to getSession call
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session fetch timeout')), 10000)
        );

        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any;

        if (error) {
          debugUtils.errorLog('Session fetch error:', error);
          throw error;
        }

        if (session?.user && isMounted) {
          debugUtils.debugLog('Session found, fetching profile...');
          const user = await fetchUserProfile(session.user.id);
          if (isMounted) {
            setUserAndClearError(user);
          }
        } else {
          debugUtils.debugLog('No session found');
          if (isMounted) {
            setUserAndClearError(null);
          }
        }
      } catch (error) {
        debugUtils.errorLog('Auth initialization error:', error);
        if (isMounted) {
          handleError(error, 'Authentication initialization');
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsInitialized(true);
          initializationComplete = true;
          if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
          }
        }
      }
    };

    initAuth();

    // Set up auth state listener - REMOVED isInitialized from dependencies to break circular dependency
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only process events after initialization is complete
      if (!isMounted || !initializationComplete) return;

      debugUtils.debugLog('Auth state change:', { event, userId: session?.user?.id });

      // Handle PASSWORD_RECOVERY event specially
      if (event === 'PASSWORD_RECOVERY') {
        debugUtils.debugLog('Password recovery event detected, session available for password reset');
        return;
      }

      // Don't set loading for INITIAL_SESSION event
      if (event === 'INITIAL_SESSION') {
        debugUtils.debugLog('Initial session event - skipping');
        return;
      }

      setIsLoading(true);

      try {
        if (session?.user) {
          debugUtils.debugLog('User authenticated, fetching profile...');
          const user = await fetchUserProfile(session.user.id);
          if (isMounted) {
            setUserAndClearError(user);
          }
        } else {
          debugUtils.debugLog('No session, clearing current user');
          if (isMounted) {
            setUserAndClearError(null);
          }
        }
      } catch (error) {
        debugUtils.errorLog('Auth state change error:', error);
        if (isMounted) {
          handleError(error, 'Authentication state change');
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      initializationComplete = true;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      subscription.unsubscribe();
    };
  }, [fetchUserProfile, handleError, setUserAndClearError]);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Attempting login for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });

      if (error) {
        debugUtils.errorLog('Supabase login error:', error);
        throw error;
      }
      
      if (!data.user) {
        throw new Error('No user data returned from login');
      }

      debugUtils.debugLog('Supabase login successful, user ID:', data.user.id);
      
      // The auth state change listener will handle fetching the profile
      // and setting the current user, so we don't need to do it here
      
    } catch (error) {
      const errorMessage = handleError(error, 'Login');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const register = useCallback(async (username: string, email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Attempting registration for:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { username: username.trim() }
        }
      });

      if (error) {
        debugUtils.errorLog('Supabase registration error:', error);
        throw error;
      }
      
      if (!data.user) {
        throw new Error('No user data returned from registration');
      }

      debugUtils.debugLog('Registration successful, user ID:', data.user.id);
      
      // The auth state change listener will handle the rest
      
    } catch (error) {
      const errorMessage = handleError(error, 'Registration');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Attempting logout...');
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        debugUtils.errorLog('Logout error:', error);
        throw error;
      }
      
      debugUtils.debugLog('Logout successful');
      setCurrentUser(null);
      
    } catch (error) {
      const errorMessage = handleError(error, 'Logout');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const resetPassword = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      debugUtils.debugLog('Attempting password reset for:', email);

      const redirectUrl = `${window.location.origin}/reset-password`;
      debugUtils.debugLog('Reset password redirect URL:', redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: redirectUrl,
      });

      if (error) {
        debugUtils.errorLog('Password reset error:', error);
        throw error;
      }

      debugUtils.debugLog('Password reset email sent successfully');

    } catch (error) {
      const errorMessage = handleError(error, 'Password reset');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const updateProfile = useCallback(async (profileData: Partial<User>) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Updating profile for user:', currentUser.id);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          username: profileData.username,
          photo_url: profileData.photoURL,
          bio: profileData.bio,
          location: profileData.location,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);

      if (error) {
        debugUtils.errorLog('Profile update error:', error);
        throw error;
      }
      
      // Update local state
      const updatedUser = { ...currentUser, ...profileData };
      const validatedUser = UserSchema.parse(updatedUser);
      setCurrentUser(validatedUser);
      
      debugUtils.debugLog('Profile updated successfully');
      
    } catch (error) {
      const errorMessage = handleError(error, 'Profile update');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, handleError]);

  const followUser = useCallback(async (userId: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Following user:', userId);
      
      const { error } = await supabase
        .from('follows')
        .insert({
          follower_id: currentUser.id,
          following_id: userId
        });

      if (error) {
        debugUtils.errorLog('Follow user error:', error);
        throw error;
      }
      
      // Refresh user profile to get updated counts
      const updatedUser = await fetchUserProfile(currentUser.id);
      setCurrentUser(updatedUser);
      
      debugUtils.debugLog('User followed successfully');
      
    } catch (error) {
      const errorMessage = handleError(error, 'Follow user');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, fetchUserProfile, handleError]);

  const unfollowUser = useCallback(async (userId: string) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    setIsLoading(true);
    setError(null);
    
    try {
      debugUtils.debugLog('Unfollowing user:', userId);
      
      const { error } = await supabase
        .from('follows')
        .delete()
        .match({
          follower_id: currentUser.id,
          following_id: userId
        });

      if (error) {
        debugUtils.errorLog('Unfollow user error:', error);
        throw error;
      }
      
      // Refresh user profile to get updated counts
      const updatedUser = await fetchUserProfile(currentUser.id);
      setCurrentUser(updatedUser);
      
      debugUtils.debugLog('User unfollowed successfully');
      
    } catch (error) {
      const errorMessage = handleError(error, 'Unfollow user');
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, fetchUserProfile, handleError]);

  const contextValue: AuthContextType = {
    currentUser,
    login,
    register,
    logout,
    resetPassword,
    updateProfile,
    followUser,
    unfollowUser,
    isLoading,
    error,
    clearError
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};